import { useState, useEffect, useContext, SyntheticEvent } from 'react';
import { Auth } from 'aws-amplify';

import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Container from '@mui/material/Container';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Stack from '@mui/material/Stack';
import { MenuItem } from '@material-ui/core';

import Store from '../../store';
import Actions from '../../store/actions';

import { submitClinicalStudyQc } from '../../api/Cder';

type Props = {
  closeEdit: () => void;
  documentId: number;
  clinicalStudySectionLink: string | null;
  documentLink: string | null;
  /** Label PDF path on S3; sent when qc_status is invalid */
  s3Path: string | null;
  initialQcStatus: string;
};

const ClinicalStudyPdfViewerModal = ({
  closeEdit,
  documentId,
  clinicalStudySectionLink,
  documentLink,
  s3Path,
  initialQcStatus
}: Props) => {
  const { dispatch } = useContext<any>(Store);
  const [tab, setTab] = useState(0);
  const [status, setStatus] = useState(initialQcStatus || 'to_qc');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setStatus(initialQcStatus || 'to_qc');
    setTab(0);
  }, [documentId, initialQcStatus]);

  const handleTabChange = (_e: SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  const handleSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      const user = await Auth.currentUserInfo();
      const qc = status.toLowerCase();
      const payload: {
        qc_status: string;
        curator_email: string;
        curator_name: string;
        s3_path?: string;
      } = {
        qc_status: qc,
        curator_email: user.attributes.email,
        curator_name: user.attributes['custom:user']
      };
      if (qc === 'invalid' && s3Path) {
        payload.s3_path = s3Path;
      }
      const res = await submitClinicalStudyQc(documentId, payload);
      const resData = res?.data;
      if (resData?.error) {
        await dispatch({
          type: Actions.SET_ALERT,
          value: { status: true, message: resData.message }
        });
      } else {
        const successMessage =
          typeof resData?.message === 'string'
            ? resData.message
            : typeof resData?.body === 'string'
              ? resData.body
              : 'Clinical study QC updated successfully';
        await dispatch({
          type: Actions.SET_ALERT,
          value: { status: true, message: successMessage, color: 'success' }
        });
        closeEdit();
      }
    } catch (e: any) {
      await dispatch({
        type: Actions.SET_ALERT,
        value: { status: true, message: e?.message ?? 'Request failed' }
      });
    } finally {
      setSubmitting(false);
    }
  };

  const renderPdfPane = (url: string | null, emptyMessage: string) => {
    if (!url) {
      return (
        <Box
          display='flex'
          justifyContent='center'
          alignItems='center'
          sx={{ height: '75vh', width: '100%' }}>
          <Typography sx={{ fontSize: '18px', fontWeight: 'bold', color: '#33B187' }}>
            {emptyMessage}
          </Typography>
        </Box>
      );
    }
    return (
      <Box display='flex' justifyContent='center' sx={{ width: '100%' }}>
        <iframe
          title='pdf'
          src={url}
          style={{ height: '75vh', width: '100%', maxWidth: '100%', border: 'none' }}
        />
      </Box>
    );
  };

  return (
    <Container maxWidth={false}>
      <DialogTitle sx={{ m: 0, p: 2 }}>
        <Typography
          component='h2'
          variant='h5'
          style={{ color: '#3AB09E', fontWeight: 'bold', fontSize: '28px' }}>
          Clinical Study QC
        </Typography>
        <IconButton
          aria-label='close'
          onClick={() => {
            closeEdit();
          }}
          sx={{
            position: 'absolute',
            right: 5,
            top: 2,
            color: 'gray.light'
          }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box component='form' onSubmit={handleSubmit} sx={{ px: 2, pb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems='center' sx={{ mb: 2 }}>
          <TextField
            variant='outlined'
            id='clinical_study_qc_status'
            label='QC Status'
            select
            required
            value={status}
            onChange={e => setStatus(e.target.value)}
            sx={{ minWidth: 260 }}
            InputProps={{
              style: { fontSize: '1.5vh' }
            }}
            InputLabelProps={{
              style: { fontSize: '1.5vh' }
            }}>
            <MenuItem value='qced'>Qced</MenuItem>
            <MenuItem value='to_qc'>To QC</MenuItem>
            <MenuItem value='escalate'>Escalate</MenuItem>
            <MenuItem value='missing details'>Missing Details</MenuItem>
            <MenuItem value='invalid'>Invalid</MenuItem>
          </TextField>
          <Button type='submit' variant='contained' color='primary' disabled={submitting}>
            {submitting ? 'Saving…' : 'Submit'}
          </Button>
        </Stack>

        <Tabs value={tab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label='Clinical Study PDF' />
          <Tab label='Full Label PDF' />
        </Tabs>

        <Grid container sx={{ mt: 1 }}>
          <Grid item xs={12}>
            {tab === 0
              ? renderPdfPane(clinicalStudySectionLink, 'No clinical study PDF available')
              : renderPdfPane(documentLink, 'No label PDF available')}
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default ClinicalStudyPdfViewerModal;
